import type { ComponentPropsWithRef, ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import { SpinnerIcon } from "../../rtl/icons";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

/** Variant colours — semantic tokens only (pairs verified by test/contrast.test.ts). */
export const buttonVariantClasses: Readonly<Record<ButtonVariant, string>> = {
  primary: "bg-action-primary text-on-action-primary hover:bg-action-primary-hover",
  secondary:
    "border border-line bg-action-secondary text-on-action-secondary hover:bg-action-secondary-hover",
  danger: "bg-action-danger text-on-action-danger hover:bg-action-danger-hover",
  ghost: "bg-transparent text-on-action-ghost hover:bg-action-ghost-hover",
};

const SIZE: Readonly<Record<ButtonSize, string>> = {
  sm: "h-9 gap-1.5 px-3 text-sm",
  md: "h-11 gap-2 px-5 text-sm",
  lg: "h-12 gap-2 px-6 text-base",
};

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Shows a spinner, sets `aria-busy` and blocks clicks; the label stays for width. */
  readonly loading?: boolean;
  /** Icon at the inline start (right in RTL). Mirroring is the icon's own concern. */
  readonly startIcon?: ReactNode;
  readonly endIcon?: ReactNode;
  readonly fullWidth?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  startIcon,
  endIcon,
  fullWidth = false,
  disabled,
  type = "button",
  className,
  children,
  onClick,
  ...rest
}: ButtonProps) {
  const { t } = useI18n();
  return (
    <button
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      className={cx(
        "relative inline-flex items-center justify-center rounded-xl font-medium whitespace-nowrap transition-colors select-none",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
        "disabled:cursor-not-allowed disabled:opacity-60",
        loading && "cursor-progress",
        buttonVariantClasses[variant],
        SIZE[size],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <SpinnerIcon className="size-4" label={t("ui.button.loading")} /> : startIcon}
      <span>{children}</span>
      {!loading && endIcon}
    </button>
  );
}
